import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useToast } from "../../hooks/use-toast";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import axios from "axios";
import {
  Users, UserPlus, LogIn, Activity, ShieldCheck, Search, Pencil, Trash2,
  CheckCircle2, XCircle, Building2, Shield, Calendar, Clock, ChevronDown,
  Key, User as UserIcon, ShieldAlert, SearchX, Loader2, ArrowRight,
  ChevronLeft, ChevronRight, Network, HardDrive, Eraser, Eye
} from "lucide-react";
import { createApiClient } from '../../Network/ApiClient';
import { usePermissions } from "../../hooks/usePermissions";
import { Card, CardContent } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../components/ui/tooltip";
import { Pagination, PaginationContent, PaginationItem } from "../../components/ui/pagination";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Switch } from "../../components/ui/switch";
import { Skeleton } from "../../components/ui/skeleton";
import { Separator } from "../../components/ui/separator";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { cn } from "../../lib/utils";

const ActionsApi = createApiClient('');

type DBRole = "super_admin" | "super_super_admin" | "system_admin" | "viewer";

interface User {
  id: number;
  name: string;
  username: string;
  companyid: number;
  company_name: string;
  role: DBRole;
  active: boolean;
}
interface ApiAction {
  id: number;
  name: string;
  type: string;
  user_id: number;
  username: string;
  user_role: string;
  timestamp: string;
  entry_name?: string;
  info?: any;
}

interface LoginEvent {
  userId: number;
  name: string;
  username: string;
  role: DBRole;
  companyName: string;
  loginAt: string;
  loginDate: string;
  loginTime: string;
}

interface UserFormData {
  name: string;
  username: string;
  password: string;
  role: DBRole;
  active: boolean;
}

const formatTimestamp = (ts: string) => {
  const d = new Date(ts);
  return {
    date: `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getFullYear()).slice(-2)}`,
    time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
  };
};

const getLocalDateString = (date: Date = new Date()) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const roleLabel: Record<DBRole, string> = {
  system_admin: "System Admin",
  super_super_admin: "Super Super Admin",
  super_admin: "Super Admin",
  viewer: "Viewer",
};

const getRoleConfig = (role: DBRole) => {
  switch (role) {
    case "system_admin": return { color: "bg-indigo-100 text-indigo-700 border-indigo-200", icon: ShieldAlert };
    case "super_super_admin": return { color: "bg-blue-100 text-blue-700 border-blue-200", icon: ShieldCheck };
    case "viewer": return { color: "bg-amber-100 text-amber-700 border-amber-200", icon: Eye };
    default: return { color: "bg-slate-100 text-slate-700 border-slate-200", icon: UserIcon };
  }
};

const ITEMS_PER_PAGE = 5;

const IGNORED_KEYS = ["id", "token", "changes", "data", "actionType", "target"];

const formatActivityTypeValue = (val: any): string => {
  const formatSingleType = (type: any): string => {
    const normalized = String(type ?? "").trim().toLowerCase();

    const activityTypeMap: Record<string, string> = {
      cpt: "CPT",
      hcpcs: "HCPCS",
      dental: "Dental",
    };

    return activityTypeMap[normalized] ?? String(type ?? "").trim();
  };

  if (Array.isArray(val)) {
    if (val.length === 0) return "—";

    return val
      .map((item: any) => {
        if (typeof item === "object" && item !== null) {
          return formatSingleType(item.type ?? item.activity_type ?? item.name ?? item);
        }

        return formatSingleType(item);
      })
      .join(", ");
  }

  return String(val)
    .split(",")
    .map(formatSingleType)
    .join(", ");
};

// ─── Shared activity-type label formatter ──────────────────────────────────
// Used everywhere a code is paired with its type (Create/Delete "Record" view
// AND Update "Changes" view, across every rule page). Keeps CPT/HCPCS
// uppercase and Dental as just-first-letter-capitalized — never full caps.
const ACTIVITY_TYPE_DISPLAY_MAP: Record<string, string> = { cpt: "CPT", hcpcs: "HCPCS", dental: "Dental" };
const formatTypeLabel = (t: any): string => {
  const norm = String(t ?? "").trim().toLowerCase();
  return ACTIVITY_TYPE_DISPLAY_MAP[norm] ?? toTitleCase(String(t ?? ""));
};

const formatValue = (val: any): string => {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "boolean") return val ? "Yes" : "No";
  if (Array.isArray(val)) {
    if (val.length === 0) return "—";
    return val.map((v: any) => {
      if (typeof v === "object" && v !== null) {
        return v.Policy_name ?? v.Provider_name ?? v.Plan_name ?? v.name ?? JSON.stringify(v);
      }
      return String(v);
    }).join(", ");
  }
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
};

const getInfoEntries = (info: any): { key: string; value: string }[] => {
  const data = info?.data ?? info ?? {};
  return Object.entries(data)
    .filter(([key]) => !IGNORED_KEYS.includes(key))
    .map(([key, val]) => ({ key, value: formatValue(val) }));
};

// ─── Title Case helper ──────────────────────────────────────────────────────
// Capitalizes only the first letter of every word, lowercases the rest.
const toTitleCase = (str: string): string => {
  if (!str) return str;
  const titled = str
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
return titled
  .replace(/\bIcd\b/g, "ICD")
  .replace(/\bIcds\b/g, "ICDs")
  .replace(/\bCpt\b/g, "CPT")
  .replace(/\bCpts\b/g, "CPTs")
  .replace(/\bHcpcs\b/g, "HCPCS")
  .replace(/\bId\b/g, "ID")
  .replace(/\bIds\b/g, "IDs")
  .replace(/\bIp\b/g, "IP")
  .replace(/\bOp\b/g, "OP");
};

const getRuleName = (name: string): string => name.replace(/^(Add|Update|Delete)\s+/i, "");

// ─── Rule name display overrides (frontend-only, does not touch backend data) ──
// Matching is done on a normalized key (lowercased, punctuation/spacing stripped)
// so backend casing/spacing differences (e.g. "activity included in another activity")
// still resolve correctly to the friendly display name.
const normalizeRuleKey = (s: string): string => (s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const RULE_NAME_MAP: Record<string, string> = {
  "Activity Included In Another Activity": "Activity Bundle",
};

const NORMALIZED_RULE_NAME_MAP: Record<string, string> = Object.entries(RULE_NAME_MAP)
  .reduce((acc, [key, val]) => {
    acc[normalizeRuleKey(key)] = val;
    return acc;
  }, {} as Record<string, string>);

const getDisplayRuleName = (name: string): string => {
  const clean = getRuleName(name);
  return NORMALIZED_RULE_NAME_MAP[normalizeRuleKey(clean)] ?? clean;
};

// Keep “and” lowercase only for the ICD Age and Gender
// and Activity Age and Gender rules.
const formatRuleDisplayName = (name: string): string => {
  const displayName = toTitleCase(getDisplayRuleName(name));
  const normalizedName = normalizeRuleKey(displayName);

  if (normalizedName === "icdageandgender") {
    return "ICD Age and Gender";
  }

  if (normalizedName === "activityageandgender") {
    return "Activity Age and Gender";
  }

  return displayName;
};

// ─── Per-page field label overrides ─────────────────────────────────────────
// Each page/rule type has its own column → label mapping, since the same
// backend key can mean something different depending on the page.
const FIELD_LABEL_MAP: Record<string, Record<string, string>> = {
  ActivitiesReqirePreApprovals: { relation_name: "Relation Name", activity_code: "Activity Code" },
  ActivityAgeGender: { relation_name: "Relation Name", activity_code: "Activity Code", gender: "Gender", is_active_to_gender: "Active To Gender", is_active_to_age: "Active To Age" },
  ActivityAllowed: { relation_name: "Relation Name", main_activity: "Activity One", allowed_activity: "Activity Two" },
  ActivityAllowedIcd: { relation_name: "Relation Name", activity: "Activity", icd: "ICD" },
  ActivityCode: { activity: "Activity", activity_description: "Activity Description", activity_type: "Activity Type" },
  ActivityCustomGroup: { name: "Name", description: "Description", activity_code: "Activity Codes" },
  ActivityCustomGrouping: { name: "Name", description: "Description", activity_code: "Activity Codes" },
  ActivityErrorsRelation: { err_msg: "Error Message" },
  ActivityFrequency: { relation_name: "Relation Name", activity_code: "Activity Code" },
  ActivityGrouping: { name: "Name", activity: "Activity", activity_type: "Activity Type", medical_activity_group: "Medical Activity Group", custom_activity_group: "Custom Activity Group", is_mixed_group: "Is Mixed Group", type: "Type" },
  ActivityLineTreatment: { relation_name: "Relation Name", activity_after: "Activity After", activity_before: "Activity Before" },
  ActivityMedicalGroup: { name: "Name", activities: "Activities", description: "Description" },
  ActivityNotAllowed: { relation_name: "Relation Name", main_activity: "Activity One", not_allowed_activity: "Activity Two" },
  ActivityNotAllowedICD: { relation_name: "Relation Name", activity: "Activity", icd: "ICD" },
  ActivityNotAllowedIncludeActivity: { relation_name: "Relation Name", main_activity: "Main Activity", not_allowed_activity: "Included Activity" },
  // Aliases: the backend has sent this rule's action "name" under different literal
  // strings over time (the old raw rule text, and now the already-renamed "Activity
  // Bundle" itself) — each needs its own normalized key pointing to the same labels.
  ActivityIncludedInAnotherActivity: { relation_name: "Relation Name", main_activity: "Main Activity", not_allowed_activity: "Included Activity" },
  ActivityBundle: { relation_name: "Relation Name", main_activity: "Main Activity", not_allowed_activity: "Included Activity" },
  ActivityNotCovered: { relation_name: "Relation Name", activity_code: "Activity Codes" },
  ActivityPayerRules: {
    relation_id: "Relation", provider_ids: "Providers", apply_to_other_providers: "Apply To Other Providers",
    networks: "Networks", policies: "Policies", patients: "Patients", active: "Active",
    apply_to_other_networks: "Apply To Other Networks", apply_to_other_policies: "Apply To Other Policies",
    apply_to_other_patients: "Apply To Other Patients",
  },
  ActivityToActivityRelation: { first_group: "First Group", second_group: "Second Group", error_id: "Error" },
  ActivityToIcdRelation: {
    icd: "ICD", custom_icd_groups: "Custom ICD Groups", medical_icd_groups: "Medical ICD Groups",
    activity_Type: "Activity Type", activity: "Activity", custom_activity_group: "Custom Activity Group",
    medical_activity_group: "Medical Activity Group", except_payers: "Except Payers", for_payers: "For Payers",
    networks: "Networks", policy: "Policy", patients: "Patients",
  },
  Benefit: { name: "Name", description: "Description", primary_diagnosis: "Primary ICD", secondary_icds: "Secondary ICD" },
  BenefitGroup: { name: "Name", description: "Description", benefits: "Benefits" },
  BenefitTemplate: { name: "Name", description: "Description", related_icds: "ICD", related_activities: "Activities" },
  CustomDrugGroups: { name: "Name", description: "Description", drugs: "Drug Codes" },
  CustomIcdGroups: { name: "Name", description: "Description", icd: "ICD" },
  Doctor: { doctor_name: "Doctor Name", doctor_id: "Doctor ID", specialty: "Specialty", status: "Status" },
  DragAgeGender: { relation_name: "Relation Name", drugs: "Drug Codes", gender: "Gender" },
  DragMedicalGroup: { name: "Name", description: "Description", drugs: "Drug Code" },
  DragNotAllowedIncludeDrug: { relation_name: "Relation Name", main_drug: "Main Drug", not_allowed_drug: "Not Allowed Drug" },
  DrugAllowedwithActivity: { relation_name: "Relation Name", drug: "Drug", activity: "Activity" },
  DrugAllowedWithDrugs: { relation_name: "Relation Name", main_drug: "Main Drug", allowed_drug: "Allowed Drug" },
  DrugAllowedwithIcd: { relation_name: "Relation Name", drugs: "Drug", icd: "ICD" },
  DrugExclusions: { relation_name: "Relation Name", drugs: "Drug Code", medical_groups: "Drug Medical Groups" },
  DrugFrequancy: { relation_name: "Relation Name", drugs: "Drugs", networks: "Networks" },
  DrugNotAllowedwithActivity: { relation_name: "Relation Name", drug: "Drug", activity: "Activity" },
  DrugNotAllowedwithIcd: { relation_name: "Relation Name", drugs: "Drug", icd: "ICD" },
  DrugNotCovered: { relation_name: "Relation Name", drugs: "Drug Code", medical_groups: "Drug Medical Groups" },
  DrugsGrouping: { name: "Name", list_of_drug_groups: "List Of Drug Groups", list_of_custom_drug_groups: "List Of Custom Drugs Groups", list_of_drugs: "List Of Drugs" },
  DrugsNotAllowedDrugs: { relation_name: "Relation Name", main_drug: "Main Drug", not_allowed_drug: "Not Allowed Drug" },
  DrugsReqirePreApprovals: { relation_name: "Relation Name", drugs: "Drug Code", medical_groups: "Drug Medical Groups" },
  Error: { err_msg: "Error Message" },
  IcdAllowed: { relation_name: "Relation Name", main_diagnoses: "Main Diagnosis", allowed_diagnoses: "Must Come Diagnosis" },
  IcdGrouping: { custom_group_name: "Group Name", icds: "List of ICD", custom_icd_groups: "Custom ICD Groups", medical_icd_groups: "Medical ICD Groups" },
  IcdNotAllowed: { relation_name: "Relation Name", main_diagnoses: "Diagnosis One", not_allowed_diagnoses: "Diagnosis Two" },
  ICDNotCovered: { relation_name: "Name", icd: "ICD" },
  Insurance: { plan_name: "Plan Name", policy_id: "Policy ID", status: "Status" },
  MedicalCodes: { icd: "ICD", description: "Description" },
  MedicalIcdGroups: { name: "Name", description: "Description", icd: "ICD", is_active: "Is Active" },
  Network: { network_name: "Networ Name", related_providers: "Related Providers" },
  NewNetwork: { network_name: "Network Name", provider: "Provider", status: "Status" },
  PatientRelation: { full_name: "Patient Name", gender: "Gender", status: "Status", patient_dob: "DOB", policy: "Policy" },
  Policy: { policy_id: "Policy ID", policy_name: "Policy Name", start_date: "Start Date", end_date: "End Date", plan: "Plan", network: "Networks", status: "Status" },
  Prescriber: { relation_name: "Relation Name", drugs: "Drug Code", specialty: "Specialty" },
  PrincipalDiagnosed: { relation_name: "Relation Name", icd: "ICD" },
  Provider: { provider_name: "Provider Name", provider_id: "Provider ID", speciality: "Speciality", encounter_checks: "Encounter", status: "Status" },
  RefilLimit: { relation_name: "Name", activity_type: "Activity Type", activity_code: "Activity Code", refill_limit: "FrequencyLimit" },
  RefillLimitDrugs: { relation_name: "Name", drugs: "Drug Code", refill_limit: "Refill Limit" },
  RelationActivityFrequency: { code: "CODE", type: "TYPE", allowedvalues: "AllowedValues", perdays: "perdays", fors: "Fors" },
  RelationAddAgeAndGender: { relation_name: "Relation Name", icd: "ICD", gender: "Gender", is_active_to_gender: "Active To Gender", is_active_to_age: "Active To Age" },
  Relationicdpayer: {
    relation_id: "RelationID", provider_ids: "ProviderIDs", apply_to_other_providers: "ApplyToOtherProviders",
    networks: "Networks", policies: "Policies", patients: "Patients", active: "Active",
    apply_to_other_networks: "ApplyToOtherNetworks", apply_to_other_policies: "ApplyToOtherPolicies",
    apply_to_other_patients: "ApplyToOtherPatients", not_active_to: "Not Active To",
  },
  Relationicdtoicd: { first_group: "FirstGroup", second_group: "SecondGroup", error_id: "ErrorId" },
  ServiceCode: { service_code: "Service Code", description: "Description", type: "Type" },
  Specialty: { name: "Specialty Name", speciality_code: "Specialty Code", status: "Status" },
  SpecialtyPage: { name: "Specialty Name", speciality_code: "Specialty Code", status: "Status" },
  SupplyLimit: { relation_name: "Name", drugs: "Drug Code", supply_limit: "Supply Limit" },
  Vip: { name: "List Name", patients: "Patients", status: "Status" },
};

// Normalize a page/rule name for loose matching: lowercase, strip spaces/punctuation
const normalizePageKey = (name: string): string =>
  (name ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Build a lookup once: normalized page name → its field map
const NORMALIZED_FIELD_LABEL_MAP: Record<string, Record<string, string>> = Object.entries(FIELD_LABEL_MAP)
  .reduce((acc, [page, fields]) => {
    acc[normalizePageKey(page)] = fields;
    return acc;
  }, {} as Record<string, Record<string, string>>);

const getFieldLabel = (key: string, actionName: string): string => {
  const normalizedActionName = normalizePageKey(getRuleName(actionName));
  const pageFields = NORMALIZED_FIELD_LABEL_MAP[normalizedActionName];

  // ICD Must Come — applies to Record, Deleted Record and fallback views.
  // The backend may send the friendly rule name "ICD Must Come" instead of
  // the internal page name "IcdAllowed", so handle both aliases explicitly.
  // Labels mirror the actual form (image: "Main Diagnosis" / "Must Come
  // Diagnosis", each with its own List of ICD / Medical Groups / Custom
  // Groups source) — never the generic "Custom Groups" / "Allowed Groups".
  if (
    normalizedActionName === "icdmustcome" ||
    normalizedActionName === "icdallowed"
  ) {
    if (key === "main_diagnoses") return "Main Diagnosis";
    if (key === "allowed_diagnoses") return "Must Come Diagnosis";
    if (key === "custom_groups" || key === "custom_icd_groups") return "Main Diagnosis - Custom Groups";
    if (key === "medical_groups" || key === "medical_icd_groups") return "Main Diagnosis - Medical Groups";
    if (key === "allowed_custom_groups" || key === "allowed_custom_icd_groups") return "Must Come Diagnosis - Custom Groups";
    if (key === "allowed_medical_groups" || key === "allowed_medical_icd_groups") return "Must Come Diagnosis - Medical Groups";
  }

  // ICD Not Allowed — applies to Record, Deleted Record and fallback views.
  // Labels mirror the actual form ("Diagnosis One" / "Diagnosis Two", each
  // with its own List of ICD / Medical Groups / Custom Groups source) —
  // never the generic "Custom Groups" / "Not Allowed Groups".
  if (normalizedActionName === "icdnotallowed") {
    if (key === "main_diagnoses") return "Diagnosis One";
    if (key === "not_allowed_diagnoses") return "Diagnosis Two";
    if (key === "custom_groups" || key === "custom_icd_groups") return "Diagnosis One - Custom Groups";
    if (key === "medical_groups" || key === "medical_icd_groups") return "Diagnosis One - Medical Groups";
    if (key === "not_allowed_custom_groups" || key === "not_allowed_custom_icd_groups") return "Diagnosis Two - Custom Groups";
    if (key === "not_allowed_medical_groups" || key === "not_allowed_medical_icd_groups") return "Diagnosis Two - Medical Groups";
  }

  // Generic merged keys used across Activity One/Two style (two-sided) rules.
  // Each page can override these via its own main_activity / allowed_activity /
  // not_allowed_activity entries in FIELD_LABEL_MAP (e.g. Activity Bundle uses
  // "Main Activity" / "Included Activity" instead of the generic "Activity One/Two").
  if (key === "activity_one") return pageFields?.main_activity ?? "Activity One";
  if (key === "activity_two") return pageFields?.allowed_activity ?? pageFields?.not_allowed_activity ?? "Activity Two";
  if (key === "activity_code_merged") return pageFields?.activity_code ?? "Activity Code";
  if (key === "network_mode") return "Network Mode";

  // Per-page override, matched loosely against the rule/page name
  if (pageFields && pageFields[key]) return pageFields[key];

  if (key === "name") {
    if (actionName.toLowerCase().includes("group")) return "Group Name";
    return "Rule Name";
  }
  if (key === "relation_name") return "Relation Name";
  return toTitleCase(key.replace(/_/g, " "));
};
// Update-dialog-only label overrides.
// Create/Delete views continue using the existing getFieldLabel logic unchanged.
const getUpdateFieldLabel = (key: string, actionName: string): string => {
  const normalizedActionName = normalizePageKey(getRuleName(actionName));

  // Activities Not Allowed Together — Update only
  if (
    normalizedActionName === "activitiesnotallowedtogether" ||
    normalizedActionName === "activitynotallowed"
  ) {
    if (key === "main_activity" || key === "activity_one") {
      return "Activity One";
    }
    if (key === "not_allowed_activity" || key === "activity_two") {
      return "Activity Two";
    }
  }

  // Activities Together — Update only
  if (
    normalizedActionName === "activitiestogether" ||
    normalizedActionName === "activityallowed"
  ) {
    if (key === "main_activity" || key === "activity_one") {
      return "Activity One";
    }
    if (key === "allowed_activity" || key === "activity_two") {
      return "Activity Two";
    }
  }

  // ICD Must Come — Update only
  if (
    normalizedActionName === "icdmustcome" ||
    normalizedActionName === "icdallowed"
  ) {
    if (key === "main_diagnoses") return "Main Diagnosis";
    if (key === "allowed_diagnoses") return "Must Come Diagnosis";
    if (key === "custom_groups" || key === "custom_icd_groups") return "Main Diagnosis - Custom Groups";
    if (key === "medical_groups" || key === "medical_icd_groups") return "Main Diagnosis - Medical Groups";
    if (key === "allowed_custom_groups" || key === "allowed_custom_icd_groups") return "Must Come Diagnosis - Custom Groups";
    if (key === "allowed_medical_groups" || key === "allowed_medical_icd_groups") return "Must Come Diagnosis - Medical Groups";
  }

  // ICD Not Allowed — Update only
  if (normalizedActionName === "icdnotallowed") {
    if (key === "main_diagnoses") return "Diagnosis One";
    if (key === "not_allowed_diagnoses") return "Diagnosis Two";
    if (key === "custom_groups" || key === "custom_icd_groups") return "Diagnosis One - Custom Groups";
    if (key === "medical_groups" || key === "medical_icd_groups") return "Diagnosis One - Medical Groups";
    if (key === "not_allowed_custom_groups" || key === "not_allowed_custom_icd_groups") return "Diagnosis Two - Custom Groups";
    if (key === "not_allowed_medical_groups" || key === "not_allowed_medical_icd_groups") return "Diagnosis Two - Medical Groups";
  }

  return getFieldLabel(key, actionName);
};

// Backend sometimes sends entry_name = the username instead of the actual
// relation_name (known backend bug on "create" actions). If entry_name
// matches the acting username, fall back to the real relation_name found
// inside info.data[0] / info.
const getRelationName = (action: ApiAction): string => {
  const dataArr = (action.info as any)?.data;
  const data = Array.isArray(dataArr) ? dataArr[0] : (action.info as any);

  return data?.relation_name
    ?? data?.name
    ?? data?.Provider_name
    ?? data?.Policy_name
    ?? (action.entry_name !== action.username ? action.entry_name : null)
    ?? "—";
};

const UserManagement: React.FC = () => {
  const { toast } = useToast();
const { isViewer, canManage, canViewLogs, isSystemAdmin: isSuperAdmin } = usePermissions();
const storedUser = (() => { try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; } })();
const currentRole = (storedUser?.role as DBRole) ?? "super_admin";
const currentCompanyId = Number(storedUser?.companyid ?? 1);

  const [activeTab, setActiveTab] = useState<string>("users");
  const [users, setUsers] = useState<User[]>([]);
  const [loginEvents, setLoginEvents] = useState<LoginEvent[]>([]);
  const [loadingUsers, setLoadingU] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [apiActions, setApiActions] = useState<ApiAction[]>([]);
  const [loadingActions, setLoadingActs] = useState(false);
  const [actionsError, setActionsError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showRoleGuide, setShowRoleGuide] = useState(false);
  const [editingUser, setEditingUser] = useState<Partial<User> | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showClearAuditConfirm, setShowClearAuditConfirm] = useState(false);
  const [clearingAudit, setClearingAudit] = useState(false);

  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoAction, setInfoAction] = useState<ApiAction | null>(null);

  const [usersPage, setUsersPage] = useState(1);
  const [loginsPage, setLoginsPage] = useState(1);
  const [actionsPage, setActionsPage] = useState(1);

  const [searchUser, setSearchUser] = useState("");
  const [filterRole, setFilterRole] = useState<DBRole | "All">("All");
  const [searchLogin, setSearchLogin] = useState("");
  const [filterDate, setFilterDate] = useState<string>(getLocalDateString());
  const [searchAudit, setSearchAudit] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingUser, setDeletingUser] = useState<{ id: number; name: string } | null>(null);


  useEffect(() => {
    if (!isSuperAdmin) return;
    try { setLoginEvents(JSON.parse(localStorage.getItem("loginEvents") || "[]")); }
    catch { setLoginEvents([]); }
  }, [isSuperAdmin]);

  const fetchUsers = useCallback(async () => {
    setLoadingU(true);
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] = res.data?.users ?? res.data?.user ?? res.data ?? [];
      setUsers(raw.map((u: any) => ({
        id: Number(u.id), name: u.name ?? "", username: u.username ?? "",
        companyid: Number(u.companyid ?? u.companyId ?? 0),
        company_name: u.company_name ?? "", role: (u.role as DBRole) ?? "employee",
        active: Boolean(u.active),
      })));
    } catch (err: any) {
      toast({ title: "Error", description: err?.response?.data?.error ?? "Failed to load users", variant: "destructive" });
    } finally { setLoadingU(false); }
  }, [toast]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const fetchActions = useCallback(async () => {
    if (!canViewLogs) return;
    setLoadingActs(true); setActionsError(null);
    try {
      const params: Record<string, string> = {};
      params.date     = filterDate || getLocalDateString();
      params.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const query = new URLSearchParams(params).toString();
      const res = await ActionsApi.get(`/actions${query ? `?${query}` : ""}`);
      const raw = res.data?.data ?? res.data ?? [];
      setApiActions(Array.isArray(raw) ? raw : []);
    } catch (err: any) {
      setActionsError(err?.response?.data?.error ?? "Failed to load action logs");
      setApiActions([]);
    } finally { setLoadingActs(false); }
  }, [canViewLogs, filterDate]);

  useEffect(() => {
    if (activeTab === "activity") fetchActions();
  }, [activeTab, fetchActions]);

  const handleSaveUser = async (form: UserFormData & { companyid: number }, id?: number) => {
    setSaving(true);
    const isEdit = id != null;
    const token = localStorage.getItem("token") ?? "";
    try {
      if (isEdit) {
        await LoginAxiosInstance.put(`/${id}`, {
          data: { name: form.name, username: form.username, role: form.role, active: form.active, companyId: form.companyid, ...(form.password ? { password: form.password } : {}) },
          token,
        });
        toast({ title: "Success", description: "User updated successfully." });
      } else {
        await LoginAxiosInstance.post("/signup", {
          data: { name: form.name, username: form.username, password: form.password, role: form.role, companyId: form.companyid },
          token,
        });
        toast({ title: "Success", description: "User created successfully." });
      }
      await fetchUsers(); setShowModal(false); setEditingUser(null);
    } catch (err: any) {
      toast({ title: "Error", description: err?.response?.data?.error ?? (isEdit ? "Update failed" : "Create failed"), variant: "destructive" });
    } finally { setSaving(false); }
  };

const handleDelete = async (id: number, name: string) => {
  setDeleting(id);
  try {
    await LoginAxiosInstance.delete(`/${id}`, {
      data: { token: localStorage.getItem("token") ?? "" },
    });
    toast({ title: "Success", description: "User deleted successfully." });
    await fetchUsers();
  } catch (err: any) {
    toast({ title: "Error", description: err?.response?.data?.error ?? "Failed to delete user", variant: "destructive" });
  } finally { setDeleting(null); }
};

  const handleAssignRole = async (userId: number, newRole: DBRole) => {
    const target = users.find(u => u.id === userId);
    if (!target || target.role === newRole) return;
    try {
      await LoginAxiosInstance.put(`/${userId}`, {
        name: target.name, username: target.username, role: newRole,
        active: target.active, companyId: target.companyid,
      });
      toast({ title: "Success", description: `${target.name} role updated to ${roleLabel[newRole]}.` });
      await fetchUsers();
    } catch (err: any) {
      toast({ title: "Error", description: err?.response?.data?.error ?? "Role update failed", variant: "destructive" });
    }
  };

  const clearLoginHistory = () => {
    localStorage.setItem("loginEvents", "[]");
    setLoginEvents([]);
    setShowClearConfirm(false);
    toast({ title: "Success", description: "Security traffic logs purged." });
  };

const handleClearAuditLogs = async () => {
    // Force a specific date — never allow a blank/undefined date to reach the API
    const dateToDelete = filterDate || getLocalDateString();
    if (!dateToDelete) {
      toast({ title: "Error", description: "Please select a date before clearing logs.", variant: "destructive" });
      return;
    }
    setClearingAudit(true);
    try {
      await ActionsApi.delete(`/actions/delete-all`, {
        params: { date: dateToDelete },
      });
      setApiActions([]);
      setShowClearAuditConfirm(false);
      toast({ title: "Success", description: `Audit logs for ${dateToDelete} cleared successfully.` });
    } catch (err: any) {
      toast({ title: "Error", description: err?.response?.data?.error ?? "Failed to clear audit logs", variant: "destructive" });
    } finally { setClearingAudit(false); }
  };

  const filteredUsers = useMemo(() => {
    const q = searchUser.toLowerCase();
    return users.filter(u =>
      (u.name.toLowerCase().includes(q) || u.username.toLowerCase().includes(q) || u.company_name.toLowerCase().includes(q) || String(u.id).includes(q))
      && (filterRole === "All" || u.role === filterRole)
    );
  }, [users, searchUser, filterRole]);

  const filteredLogins = useMemo(() => {
    const q = searchLogin.toLowerCase();
    return loginEvents.filter(e => e.name.toLowerCase().includes(q) || e.username.toLowerCase().includes(q) || e.companyName.toLowerCase().includes(q));
  }, [loginEvents, searchLogin]);

  const paginatedUsers = useMemo(() => filteredUsers.slice((usersPage - 1) * ITEMS_PER_PAGE, usersPage * ITEMS_PER_PAGE), [filteredUsers, usersPage]);
  const paginatedLogins = useMemo(() => filteredLogins.slice((loginsPage - 1) * ITEMS_PER_PAGE, loginsPage * ITEMS_PER_PAGE), [filteredLogins, loginsPage]);

  const filteredActions = useMemo(() => {
    const q = searchAudit.toLowerCase().trim();
    if (!q) return apiActions;
    return apiActions.filter(a =>
      (a.username ?? "").toLowerCase().includes(q) ||
      String(a.user_id).includes(q) ||
      (a.type ?? "").toLowerCase().includes(q) ||
      (a.name ?? "").toLowerCase().includes(q) ||
      (a.info?.data?.name ?? a.info?.name ?? "").toLowerCase().includes(q) ||
      (a.info?.data?.relation_name ?? a.info?.relation_name ?? "").toLowerCase().includes(q)
    );
  }, [apiActions, searchAudit]);

  const paginatedActions = useMemo(() => filteredActions.slice((actionsPage - 1) * ITEMS_PER_PAGE, actionsPage * ITEMS_PER_PAGE), [filteredActions, actionsPage]);

  // ─── UserForm ─────────────────────────────────────────────────────────────
  const UserForm = ({ initialData, onSave, onCancel, isSaving }: any) => {
    const isEdit = initialData?.id != null;
    const [form, setForm] = useState<UserFormData>({
      name: initialData?.name ?? "", username: initialData?.username ?? "",
      password: "", role: initialData?.role ?? "employee", active: initialData?.active ?? true,
    });
    return (
      <div className="space-y-6 pt-4">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-5">
            <div className="space-y-2.5">
              <Label htmlFor="name" className="text-[11px] font-black tracking-widest text-slate-400 pl-1">Full Name</Label>
              <Input id="name" value={form.name} className="h-12 pl-4 bg-slate-50 border-slate-100 rounded-2xl focus:bg-white transition-all"
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Enter Full name" />
            </div>
            <div className="space-y-2.5">
              <Label htmlFor="username" className="text-[11px] font-black tracking-widest text-slate-400 pl-1">System username</Label>
              <Input id="username" value={form.username} className="h-12 pl-4 bg-slate-50 border-slate-100 rounded-2xl focus:bg-white transition-all"
                onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="any user" />
            </div>
          </div>
          <div className="space-y-2.5">
            <Label htmlFor="password" className="text-[11px] font-black tracking-widest text-slate-400 pl-1">
              {isEdit ? "Password (optional)" : "Password"}
            </Label>
            <Input id="password" type="password" className="h-12 pl-4 bg-slate-50 border-slate-100 rounded-2xl focus:bg-white transition-all"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••••••" />
          </div>
          <div className="grid grid-cols-2 gap-8 pt-4">
            <div className="space-y-3">
              <Label className="text-[11px] font-black tracking-widest text-slate-400 pl-1">Role</Label>
              <div className="flex flex-col gap-2">
{(["super_admin", "super_super_admin", "system_admin", "viewer"] as DBRole[]).map(r => {
                    const isActive = form.role === r;
                  return (
                    <button key={r} type="button" onClick={() => setForm({ ...form, role: r })}
                      className={cn("flex items-center gap-3 px-4 py-3 rounded-2xl border-2 transition-all text-left group",
                        isActive ? "bg-slate-900 border-slate-900 text-white shadow-xl shadow-slate-200" : "bg-white border-slate-100 text-slate-500 hover:border-slate-200")}>
                      <div className={cn("w-5 h-5 rounded-full border-4 flex items-center justify-center transition-all", isActive ? "border-white bg-slate-900" : "border-slate-100")}>
                        {isActive && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </div>
                      <span className="text-xs font-black tracking-tighter capitalize">{r.replaceAll("_", " ").toLowerCase()}</span>
                      <ArrowRight className={cn("ml-auto w-3.5 h-3.5 opacity-0 group-hover:opacity-40 transition-opacity", isActive && "hidden")} />
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-[11px] font-black tracking-widest text-slate-400 pl-1">State</Label>
              <div className={cn("rounded-[2rem] p-8 flex flex-col items-center justify-center text-center gap-4 border-2 transition-all duration-500",
                form.active ? "bg-emerald-50/50 border-emerald-100" : "bg-slate-50 border-slate-100")}>
                <div className={cn("w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500",
                  form.active ? "bg-emerald-100 text-emerald-600 shadow-lg shadow-emerald-100" : "bg-slate-100 text-slate-300")}>
                  {form.active ? <CheckCircle2 className="w-8 h-8" /> : <XCircle className="w-8 h-8" />}
                </div>
                <div>
                  <p className={cn("text-xs font-black tracking-widest", form.active ? "text-emerald-700" : "text-slate-400")}>{form.active ? "Active link" : "Node offline"}</p>
                  <p className="text-[10px] text-slate-400 font-medium mt-1">Status affects all API access permissions.</p>
                </div>
                <Switch checked={form.active} onCheckedChange={(val) => setForm({ ...form, active: val })} className="data-[state=checked]:bg-emerald-500 scale-125" />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="pt-6 border-t border-slate-100 gap-3">
          <Button variant="ghost" onClick={onCancel} disabled={isSaving} className="rounded-2xl h-12 text-slate-400 font-bold px-8">Discard</Button>
          <Button onClick={() => onSave({ ...form, companyid: currentCompanyId }, initialData?.id)}
            disabled={isSaving || !form.name || !form.username || (!isEdit && !form.password)}
            className="rounded-2xl h-12 px-10 bg-slate-900 hover:bg-slate-800 shadow-2xl shadow-slate-200">
            {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            {isEdit ? "Save Changes" : "Create User"}
          </Button>
        </DialogFooter>
      </div>
    );
  };

  // ─── PaginationControls ───────────────────────────────────────────────────
  const PaginationControls = ({ total, current, setPage }: { total: number; current: number; setPage: (p: number) => void }) => {
    const totalPages = Math.ceil(total / ITEMS_PER_PAGE);
    if (totalPages <= 1) return null;
    const getVisiblePages = () => {
      const delta = 2;
      const range: number[] = [];
      for (let i = Math.max(1, current - delta); i <= Math.min(totalPages, current + delta); i++) range.push(i);
      return range;
    };
    const visiblePages = getVisiblePages();
    return (
      <div className="flex items-center justify-between px-10 py-5 bg-slate-50/50 border-t border-slate-100">
        <p className="text-xs font-bold text-slate-400 shrink-0">
          Showing <span className="text-slate-700">{(current - 1) * ITEMS_PER_PAGE + 1}</span> to{" "}
          <span className="text-slate-700">{Math.min(current * ITEMS_PER_PAGE, total)}</span> of{" "}
          <span className="text-slate-700">{total}</span> entries
        </p>
        <Pagination className="w-auto mx-0">
          <PaginationContent className="gap-1 flex-wrap justify-end">
            <PaginationItem>
              <Button variant="outline" size="icon" className="h-8 w-8 rounded-lg border-slate-200"
                onClick={() => setPage(Math.max(1, current - 1))} disabled={current === 1}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </PaginationItem>
            {visiblePages[0] > 1 && (
              <>
                <PaginationItem>
                  <Button variant="outline" className="h-8 w-8 p-0 rounded-lg text-xs font-bold border-slate-200 text-slate-400" onClick={() => setPage(1)}>1</Button>
                </PaginationItem>
                {visiblePages[0] > 2 && <span className="text-slate-300 text-xs px-1">...</span>}
              </>
            )}
            {visiblePages.map(i => (
              <PaginationItem key={i}>
                <Button variant={current === i ? "default" : "outline"}
                  className={cn("h-8 w-8 p-0 rounded-lg text-xs font-bold transition-all",
                    current === i ? "bg-slate-900 shadow-lg shadow-slate-200" : "border-slate-200 text-slate-400 hover:text-slate-900")}
                  onClick={() => setPage(i)}>{i}</Button>
              </PaginationItem>
            ))}
            {visiblePages[visiblePages.length - 1] < totalPages && (
              <>
                {visiblePages[visiblePages.length - 1] < totalPages - 1 && <span className="text-slate-300 text-xs px-1">...</span>}
                <PaginationItem>
                  <Button variant="outline" className="h-8 w-8 p-0 rounded-lg text-xs font-bold border-slate-200 text-slate-400" onClick={() => setPage(totalPages)}>{totalPages}</Button>
                </PaginationItem>
              </>
            )}
            <PaginationItem>
              <Button variant="outline" size="icon" className="h-8 w-8 rounded-lg border-slate-200"
                onClick={() => setPage(Math.min(totalPages, current + 1))} disabled={current === totalPages}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    );
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#F8FAFC] p-8 pb-24 font-sans selection:bg-slate-900 selection:text-white">
        <Toaster />
        <div className="max-w-7xl mx-auto space-y-12 animate-in fade-in duration-700">

          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 pb-4">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="bg-slate-900 p-2.5 rounded-2xl shadow-2xl shadow-slate-200">
                  <Network className="w-5 h-5 text-white" />
                </div>
                <Badge variant="outline" className="bg-white border-slate-200 text-slate-400 text-[9px] font-black tracking-[0.2em] px-3 h-6 rounded-lg">
                  Infrastructure v2.4
                </Badge>
              </div>
              <div className="space-y-1">
                <h1 className="text-5xl font-black text-slate-900 tracking-tighter">Identity core</h1>
                <p className="text-slate-500 font-medium text-lg max-w-2xl leading-relaxed">
                  Comprehensive network governance, authentication auditing, and entity state orchestration.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-white border border-slate-200 p-1.5 rounded-[1.5rem] shadow-sm flex items-center gap-4">
                <div className="flex items-center gap-3 pl-4 pr-2 py-2 border-r border-slate-100">
                  <div className={cn("p-2 rounded-xl", getRoleConfig(currentRole).color)}>
                    <Shield className="w-4 h-4" />
                  </div>
                  <div className="flex flex-col leading-tight pr-4">
                    <span className="text-[9px] font-black text-slate-400 tracking-widest">Authority</span>
                    <span className="text-sm font-black text-slate-800">{roleLabel[currentRole]}</span>
                  </div>
                </div>
                {canManage && (
                  <Button onClick={() => { setEditingUser(null); setShowModal(true); }}
                    className="h-12 px-8 rounded-2xl bg-primary hover:bg-slate-800 shadow-xl shadow-primary/20 gap-2 font-black text-xs tracking-widest">
                    <UserPlus className="w-4 h-4" />
                    New entity
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
            {[
              { label: "Network entities", val: users.length, icon: Users, color: "text-blue-600", bg: "bg-blue-50" },
              { label: "Active channels", val: users.filter(u => u.active).length, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
              { label: "Privileged nodes", val: users.filter(u => u.role === "super_super_admin").length, icon: Shield, color: "text-indigo-600", bg: "bg-indigo-50" },
              { label: "Access vectors", val: loginEvents.length, icon: LogIn, color: "text-purple-600", bg: "bg-purple-50", hide: !isSuperAdmin },
              { label: "Audit payload", val: apiActions.length, icon: Activity, color: "text-amber-600", bg: "bg-amber-50", hide: !canViewLogs },
            ].filter(s => !s.hide).map((s, i) => (
              <Card key={i} className="border-none shadow-sm shadow-slate-200/50 rounded-[2rem] bg-white group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <CardContent className="p-7 flex flex-col gap-4">
                  <div className={cn("w-12 h-12 rounded-2xl flex items-center justify-center transition-transform group-hover:scale-110", s.bg)}>
                    <s.icon className={cn("w-6 h-6", s.color)} />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-slate-900 tracking-tighter leading-none mb-1.5">{loadingUsers ? "..." : s.val}</p>
                    <p className="text-[10px] font-black text-slate-400 tracking-widest">{s.label}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setUsersPage(1); setLoginsPage(1); setActionsPage(1); }} className="space-y-10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 border-b border-slate-100 pb-0">
              <TabsList className="bg-transparent h-auto p-0 gap-10">
                {[
                  { id: "users", label: "Registry", icon: Users },
                  { id: "logins", label: "Traffic", icon: LogIn, count: loginEvents.length, hide: !isSuperAdmin },
                  { id: "activity", label: "Audits", icon: Activity, hide: !canViewLogs },
                ].filter(t => !t.hide).map(t => (
                  <TabsTrigger key={t.id} value={t.id}
                    className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-slate-900 border-b-4 border-transparent data-[state=active]:border-slate-900 rounded-none px-0 pb-6 text-base font-black text-slate-400 transition-all gap-3 tracking-tighter">
                    <t.icon className="w-4 h-4" />
                    {t.label}
                    {t.count ? <span className="ml-1 px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-500 text-[10px] font-bold">{t.count}</span> : null}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {/* ── TAB 1 — REGISTRY ── */}
            <TabsContent value="users" className="m-0 focus-visible:ring-0">
              <div className="animate-in fade-in duration-500">
                <Card className="border-none shadow-2xl shadow-slate-200/60 rounded-[2.5rem] overflow-hidden bg-white">
                  <Table className="table-fixed w-full">
                    <TableHeader className="bg-slate-50/40">
                      <TableRow className="hover:bg-transparent border-slate-100/50">
                        <TableHead className="font-bold text-[13px] py-6 text-slate-700 w-[35%]"><span className="pl-16">System entity</span></TableHead>
                        <TableHead className="font-bold text-[13px] py-6 text-slate-700 w-[15%] text-center">Governance</TableHead>
                        <TableHead className="font-bold text-[13px] py-6 text-slate-700 w-[15%] text-center">Authority</TableHead>
                        <TableHead className="font-bold text-[13px] py-6 text-slate-700 w-[15%] text-center">Connectivity</TableHead>
                        {canManage && <TableHead className="font-bold text-[13px] py-6 text-slate-700 w-[20%] text-center">Identity management</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loadingUsers ? (
                        [1, 2, 3, 4, 5].map(i => (
                          <TableRow key={i} className="border-slate-50">
                            <TableCell className="py-6 text-center"><Skeleton className="h-12 w-48 rounded-xl mx-auto" /></TableCell>
                            <TableCell className="text-center"><Skeleton className="h-4 w-32 rounded-lg mx-auto" /></TableCell>
                            <TableCell className="text-center"><Skeleton className="h-8 w-24 rounded-xl mx-auto" /></TableCell>
                            <TableCell className="text-center"><Skeleton className="h-5 w-20 rounded-lg mx-auto" /></TableCell>
                            {canManage && <TableCell className="text-center"><Skeleton className="h-10 w-24 mx-auto rounded-xl" /></TableCell>}
                          </TableRow>
                        ))
                      ) : paginatedUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={canManage ? 5 : 4} className="h-96 text-center">
                            <div className="flex flex-col items-center justify-center gap-4 grayscale opacity-40">
                              <SearchX className="w-16 h-16 text-slate-900" />
                              <p className="text-xl font-black text-slate-900 tracking-tighter">Null Result Vector</p>
                              <p className="text-sm text-slate-400 font-medium max-w-xs leading-relaxed">No identities matched the query parameters.</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedUsers.map(u => {
                          const roleCfg = getRoleConfig(u.role);
                          return (
                            <TableRow key={u.id} className="group hover:bg-slate-50/40 transition-colors border-slate-50/50">
                              <TableCell className="py-6 pl-10">
                                <div className="flex items-center gap-5">
                                  <div className="relative">
                                    <Avatar className="h-12 w-12 rounded-2xl shadow-xl shadow-slate-200 border-2 border-white ring-1 ring-slate-100 group-hover:rotate-6 transition-all duration-500">
                                      <AvatarFallback className="rounded-2xl font-black text-sm bg-slate-900 text-white">
                                        {u.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className={cn("absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white shadow-sm", u.active ? "bg-emerald-500" : "bg-slate-300")} />
                                  </div>
                                  <div className="flex flex-col text-left">
                                    <span className="font-black text-slate-900 tracking-tight text-base group-hover:text-primary transition-colors">{toTitleCase(u.name)}</span>
                                    <span className="text-[11px] font-bold text-slate-400 tracking-wide">ID: {u.id.toString().padStart(4, '0')} · @{u.username.toLowerCase()}</span>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex items-center gap-3 justify-center">
                                  <div className="p-1.5 bg-slate-50 rounded-lg group-hover:bg-blue-50 transition-colors">
                                    <Building2 className="w-3.5 h-3.5 text-slate-400 group-hover:text-blue-500" />
                                  </div>
                                  <span className="text-sm font-bold text-slate-600">{toTitleCase(u.company_name) || "Central core"}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge className={cn("rounded-xl px-3 py-1 text-[10px] font-black tracking-tighter shadow-sm border-none pointer-events-none", roleCfg.color)}>
                                  {roleLabel[u.role]}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <div className={cn("inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black tracking-widest transition-all",
                                  u.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400")}>
                                  <div className={cn("w-1.5 h-1.5 rounded-full", u.active ? "bg-emerald-500 animate-pulse" : "bg-slate-300")} />
                                  {u.active ? "Synced" : "Paused"}
                                </div>
                              </TableCell>
                              {canManage && (
                                <TableCell className="text-center">
                                  <div className="flex items-center justify-center gap-3">
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-10 w-10 rounded-2xl hover:bg-slate-900 hover:text-white transition-all shadow-sm hover:shadow-xl hover:shadow-slate-200"
                                          onClick={() => { setEditingUser(u); setShowModal(true); }}>
                                          <Pencil className="w-4 h-4" />
                                        </Button>
                                      </TooltipTrigger>
                                      <TooltipContent className="bg-slate-900 text-white rounded-xl border-none">Modify Identity</TooltipContent>
                                    </Tooltip>
                                    {u.role !== "system_admin" && (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button variant="ghost" size="icon" disabled={deleting === u.id}
                                            className="h-10 w-10 rounded-2xl hover:bg-red-500 hover:text-white transition-all shadow-sm hover:shadow-xl hover:shadow-red-100"
                                            onClick={() => { setDeletingUser({ id: u.id, name: u.name }); setShowDeleteConfirm(true); }}>
                                            {deleting === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent className="bg-red-500 text-white rounded-xl border-none">Revoke Access</TooltipContent>
                                      </Tooltip>
                                    )}
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                  <PaginationControls total={filteredUsers.length} current={usersPage} setPage={setUsersPage} />
                </Card>
              </div>
            </TabsContent>

            {/* ── TAB 2 — TRAFFIC ── */}
            <TabsContent value="logins" className="m-0 focus-visible:ring-0">
              <div className="animate-in fade-in duration-500">
                <Card className="border-none shadow-2xl shadow-slate-200/60 rounded-[2.5rem] overflow-hidden bg-white">
                  <Table className="table-fixed w-full">
                    <TableHeader className="bg-slate-50/40">
                      <TableRow className="hover:bg-transparent border-slate-100/50">
                        <TableHead className="font-bold text-[13px] py-6 text-slate-700 w-[40%]"><span className="pl-10">Session operator</span></TableHead>
                        <TableHead className="font-bold text-[13px] py-6 text-slate-700 w-[35%]"><span className="pl-6">Authentication vector</span></TableHead>
                        <TableHead className="font-bold text-[13px] py-6 text-slate-700 w-[25%] text-right"><span className="pr-10">Clearance</span></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedLogins.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="h-96 text-center">
                            <div className="flex flex-col items-center justify-center gap-4 opacity-30 grayscale">
                              <LogIn className="w-16 h-16 text-slate-900" />
                              <p className="text-xl font-black text-slate-900 tracking-tighter">Null traffic vector</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedLogins.map((e, i) => {
                          const matchedUser = users.find(u => u.username === e.username || u.id === e.userId);
                          return (
                            <TableRow key={i} className="hover:bg-slate-50/30 border-slate-50/50">
                              <TableCell className="pl-10 py-3">
                                <div className="flex items-center gap-4">
                                  <div className="h-10 w-10 rounded-2xl bg-slate-100 flex items-center justify-center font-black text-xs text-slate-400 shadow-inner">
                                    {e.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
                                  </div>
                                  <div className="flex flex-col leading-tight">
                                    <span className="font-black text-slate-800 text-sm tracking-tight">{toTitleCase(e.name)}</span>
                                    <span className="text-[10px] text-slate-400 font-bold tracking-tighter">{toTitleCase(e.companyName)}</span>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="pl-6">
                                <div className="flex items-center gap-6">
                                  <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                                    <Calendar className="w-3.5 h-3.5 opacity-30" />{e.loginDate}
                                  </div>
                                  <div className="flex items-center gap-2 text-xs font-bold text-slate-500">
                                    <Clock className="w-3.5 h-3.5 opacity-30" />{e.loginTime}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-right pr-10">
                                {matchedUser && matchedUser.role !== "system_admin" ? (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="outline" className="h-10 rounded-xl px-4 border-slate-200 text-[10px] font-black tracking-widest gap-3 shadow-sm hover:bg-white hover:text-slate-700">
                                        {roleLabel[matchedUser.role]}<ChevronDown className="w-3.5 h-3.5 opacity-40" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56 rounded-2xl p-3 shadow-2xl border-slate-100">
                                      <DropdownMenuLabel className="text-[10px] font-black tracking-[0.2em] text-slate-400 pb-3 pl-3">Modify clearance</DropdownMenuLabel>
                                      <DropdownMenuSeparator className="bg-slate-50" />
                                      {(["super_admin", "super_super_admin", "system_admin"] as DBRole[]).map(r => (
                                        <DropdownMenuItem key={r}
                                          className={cn("rounded-xl font-bold text-xs py-2.5 px-3 transition-all", matchedUser.role === r ? "bg-slate-900 text-white shadow-xl" : "text-slate-600 hover:bg-slate-50")}
                                          onClick={() => handleAssignRole(matchedUser.id, r)}>
                                          {roleLabel[r]}
                                          {matchedUser.role === r && <CheckCircle2 className="w-4 h-4 ml-auto text-emerald-400" />}
                                        </DropdownMenuItem>
                                      ))}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                ) : (
                                  <Badge className={cn("rounded-xl px-3 py-1.5 text-[9px] font-black tracking-[0.15em] shadow-none border-none pointer-events-none", getRoleConfig(e.role).color)}>
                                    {roleLabel[e.role]}
                                  </Badge>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                  <PaginationControls total={filteredLogins.length} current={loginsPage} setPage={setLoginsPage} />
                </Card>
              </div>
            </TabsContent>

            {/* ── TAB 3 — AUDITS ── */}
            <TabsContent value="activity" className="m-0 focus-visible:ring-0">
              <div className="animate-in fade-in duration-500 space-y-4">

                {/* ── Filters ── */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  {/* Date */}
                  <div className="flex items-center gap-2 h-10 px-3.5 rounded-2xl border border-slate-200 bg-white shadow-sm focus-within:ring-2 focus-within:ring-slate-900/10 transition-all">
                    <Calendar className="w-4 h-4 text-slate-400 shrink-0 pointer-events-none" />
                    <input
                      type="date"
                      value={filterDate}
                      max={getLocalDateString()}
                      onChange={(e) => { setFilterDate(e.target.value); setActionsPage(1); }}
                      className="bg-transparent text-xs font-bold text-slate-700 outline-none border-none cursor-pointer"
                    />
                  </div>
                  {/* Single search */}
                  <div className="flex items-center gap-2 h-10 px-3.5 rounded-2xl border border-slate-200 bg-white shadow-sm flex-1 min-w-[200px] focus-within:ring-2 focus-within:ring-slate-900/10 transition-all">
                    <Search className="w-4 h-4 text-slate-400 shrink-0" />
                    <input
                      value={searchAudit}
                      onChange={(e) => { setSearchAudit(e.target.value); setActionsPage(1); }}
                      placeholder="Search by username, ID, name or type…"
                      className="flex-1 bg-transparent text-xs font-bold text-slate-700 placeholder:text-slate-400 outline-none border-none"
                    />
                    {searchAudit && (
                      <button onClick={() => { setSearchAudit(""); setActionsPage(1); }} className="text-slate-300 hover:text-slate-500 transition-colors">
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {/* Today reset */}
                  {filterDate !== getLocalDateString() && (
                    <Button
                      variant="outline" size="sm"
                      onClick={() => { setFilterDate(getLocalDateString()); setActionsPage(1); }}
                      className="h-10 rounded-2xl border-slate-200 text-xs font-black text-slate-500 hover:text-slate-900 shrink-0"
                    >
                      Today
                    </Button>
                  )}
                  <div className="flex-1" />
                  {/* Clear Logs button — System Admin only */}
                  {isSuperAdmin && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => setShowClearAuditConfirm(true)}
                          disabled={apiActions.length === 0 || loadingActions}
                          className="h-10 rounded-2xl border-red-200 bg-red-50 text-red-600 hover:bg-red-100 hover:border-red-300 text-xs font-black gap-2 shrink-0 transition-all shadow-sm"
                        >
                          <Eraser className="w-3.5 h-3.5" />
                          Clear Logs
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-red-600 text-white rounded-xl border-none">
                        Delete all audit logs for selected date
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>

                <Card className="border-none shadow-2xl shadow-slate-200/60 rounded-[3rem] overflow-hidden bg-white">
                  <Table className="table-fixed w-full">
                    <TableHeader className="bg-slate-900">
                      <TableRow className="hover:bg-transparent border-slate-800">
                     <TableHead className="font-bold text-[13px] py-6 text-white w-[22%]"><span className="pl-16">User Name</span></TableHead>
<TableHead className="pl-6 font-bold text-[13px] py-6 text-white w-[18%]">Rule Details</TableHead>
<TableHead className="font-bold text-[13px] py-6 text-white w-[13%]"><span className="pl-6">Actions</span></TableHead>
<TableHead className="pl-6 font-bold text-[13px] py-6 text-white w-[22%]">Relation Name</TableHead>
<TableHead className="font-bold text-[13px] py-6 text-white w-[13%] text-center">Time Stamp</TableHead>
<TableHead className="font-bold text-[13px] py-6 text-white w-[12%] text-center">View</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loadingActions ? (
                        [1, 2, 3, 4, 5, 6].map(i => (
                          <TableRow key={i} className="border-slate-50">
                            <TableCell className="pl-10 py-6"><Skeleton className="h-10 w-40 rounded-xl" /></TableCell>
                            <TableCell className="pl-6"><Skeleton className="h-6 w-20 rounded-lg" /></TableCell>
                            <TableCell className="pl-6"><Skeleton className="h-4 w-32 rounded-md" /></TableCell>
                            <TableCell className="pl-6"><Skeleton className="h-4 w-48 rounded-md" /></TableCell>
                            <TableCell className="text-center"><Skeleton className="h-8 w-20 mx-auto rounded-lg" /></TableCell>
                            <TableCell className="text-center"><Skeleton className="h-8 w-16 mx-auto rounded-lg" /></TableCell>
                          </TableRow>
                        ))
                      ) : actionsError ? (
                        <TableRow>
                          <TableCell colSpan={6} className="h-96 text-center">
                            <div className="flex flex-col items-center justify-center gap-4">
                              <ShieldAlert className="w-16 h-16 text-red-500 opacity-20" />
                              <p className="text-xl font-black text-slate-900 tracking-tighter">Protocol failure</p>
                              <p className="text-sm text-slate-400 font-bold italic">{actionsError}</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : paginatedActions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="h-96 text-center">
                            <div className="flex flex-col items-center justify-center gap-4 opacity-20">
                              <HardDrive className="w-16 h-16 text-slate-900" />
                              <p className="text-xl font-black text-slate-900 tracking-tighter">No transaction logs</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedActions.map((action) => {
                          const { date, time } = formatTimestamp(action.timestamp);
                          return (
                            <TableRow key={action.id} className="hover:bg-slate-50/50 border-slate-50/50">
                              <TableCell className="pl-10 py-3">
                                <div className="flex items-center gap-4">
                                  <div className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center font-black text-[10px] text-white">
                                    {(action.username ?? "?").slice(0, 2).toUpperCase()}
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="font-bold text-slate-800 text-sm tracking-tight leading-none">
                                      {toTitleCase(action.username ?? "—")}
                                    </span>
                                    <span className="text-[10px] text-slate-400 font-black tracking-tighter mt-1 lowercase">
                                      @{action.username ?? "system"}
                                    </span>
                                  </div>
                                </div>
                              </TableCell>
                        <TableCell className="pl-6 text-xs font-bold text-slate-500">
  {formatRuleDisplayName(action.name)}
</TableCell>
<TableCell className="pl-6">
  <Badge className="bg-slate-100 text-slate-900 border-none rounded-lg text-[9px] font-black tracking-widest px-2 py-1 shadow-none hover:bg-slate-100">
    {toTitleCase(action.type)}
  </Badge>
</TableCell>

                              <TableCell className="pl-6 text-xs font-bold text-slate-600">
                                {toTitleCase(getRelationName(action))}
                              </TableCell>
                              <TableCell className="text-center">
                                <div className="flex flex-col items-center gap-0.5">
                                  <span className="text-xs font-black text-slate-800 tracking-tight">{date}</span>
                                  <span className="text-[10px] font-bold text-slate-400">{time}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                {action.info ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button variant="ghost" size="sm"
                                        onClick={() => { setInfoAction(action); setShowInfoModal(true); }}
                                        className="h-8 w-8 p-0 rounded-xl bg-slate-100 hover:bg-slate-900 hover:text-white text-slate-600 transition-all">
                                        <Eye className="w-4 h-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-slate-900 text-white rounded-xl border-none">View Details</TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-slate-200 text-xs">—</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                  <PaginationControls total={filteredActions.length} current={actionsPage} setPage={setActionsPage} />
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* ── Modals ── */}
        <Dialog open={showRoleGuide} onOpenChange={setShowRoleGuide}>
          <DialogContent className="sm:max-w-5xl rounded-[3rem] p-10 border-none shadow-2xl">
            <DialogHeader className="text-center max-w-xl mx-auto mb-10 space-y-4">
              <DialogTitle className="text-3xl font-black text-slate-900 tracking-tighter">Access matrix</DialogTitle>
              <DialogDescription className="text-slate-500 font-medium leading-relaxed">Defining cross-module authority bounds for all authenticated system nodes.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 rounded-[3rem] bg-slate-100/50 border border-slate-200">
              <div className="hidden md:block" />
              <div className="text-center p-6 bg-slate-900 rounded-[2rem] text-white shadow-2xl">
                <ShieldAlert className="w-6 h-6 mx-auto mb-3 text-indigo-400" />
                <p className="text-[10px] font-black tracking-[0.2em] opacity-40">Tier 01</p>
                <p className="font-black tracking-tight">System Admin</p>
              </div>
              <div className="text-center p-6 bg-white rounded-[2rem] border border-slate-200 shadow-sm">
                <ShieldCheck className="w-6 h-6 mx-auto mb-3 text-blue-500" />
                <p className="text-[10px] font-black tracking-[0.2em] text-slate-300">Tier 02</p>
                <p className="font-black tracking-tight text-slate-700">Super Super Admin</p>
              </div>
              <div className="text-center p-6 bg-white rounded-[2rem] border border-slate-200 shadow-sm">
                <UserIcon className="w-6 h-6 mx-auto mb-3 text-emerald-500" />
                <p className="text-[10px] font-black tracking-[0.2em] text-slate-300">Tier 03</p>
                <p className="font-black tracking-tight text-slate-700">Super Admin</p>
              </div>
              {[
                ["Core governance", true, true, false],
                ["Identity provisioning", true, false, false],
                ["Security overrides", true, false, false],
                ["Global orchestration", true, false, false],
                ["Intrusion auditing", true, true, false],
              ].map(([label, sa, a, e], idx) => (
                <React.Fragment key={idx}>
                  <div className="flex items-center px-8 font-black text-xs text-slate-800 tracking-tight">{label as string}</div>
                  <div className="flex justify-center items-center py-6 bg-slate-900/5 rounded-3xl">{sa ? <CheckCircle2 className="w-5 h-5 text-indigo-500" /> : <XCircle className="w-5 h-5 text-slate-200" />}</div>
                  <div className="flex justify-center items-center py-6 bg-white rounded-3xl border border-slate-100">{a ? <CheckCircle2 className="w-5 h-5 text-blue-500" /> : <XCircle className="w-5 h-5 text-slate-100" />}</div>
                  <div className="flex justify-center items-center py-6 bg-white rounded-3xl border border-slate-100">{e ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <XCircle className="w-5 h-5 text-slate-100" />}</div>
                </React.Fragment>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showModal} onOpenChange={(open) => { setShowModal(open); if (!open) setEditingUser(null); }}>
          <DialogContent className="sm:max-w-2xl rounded-[3rem] border-none shadow-2xl animate-in zoom-in-95 p-0 overflow-hidden max-h-[90vh] flex flex-col">
            <div className="p-8 pb-0">
              <DialogHeader className="space-y-2 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center shadow-lg">
                    {editingUser ? <Pencil className="w-4 h-4 text-white" /> : <UserPlus className="w-4 h-4 text-white" />}
                  </div>
                  <DialogTitle className="text-3xl font-black text-slate-900 tracking-tighter">
                    {editingUser ? "Edit User" : "Add New User"}
                  </DialogTitle>
                </div>
              </DialogHeader>
              <Separator className="bg-slate-50" />
            </div>
            <div className="overflow-y-auto flex-1 px-8 pb-8">
              <UserForm initialData={editingUser} isSaving={saving} onSave={handleSaveUser} onCancel={() => setShowModal(false)} />
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
          <DialogContent className="sm:max-w-md rounded-[3rem] p-10 border-none shadow-2xl animate-in slide-in-from-top-4">
            <DialogHeader className="items-center text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center animate-pulse">
                <ShieldAlert className="w-10 h-10 text-red-600" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-black text-slate-900 tracking-tighter">Purge Stream Data?</DialogTitle>
                <DialogDescription className="text-slate-400 font-medium leading-relaxed px-4">
                  You are executing a permanent purge of authentication history. This metadata cannot be recovered from the local buffer.
                </DialogDescription>
              </div>
            </DialogHeader>
            <DialogFooter className="sm:justify-center gap-3 pt-8">
              <Button variant="ghost" onClick={() => setShowClearConfirm(false)} className="rounded-2xl h-12 w-full sm:w-32 font-bold text-slate-400">Abort</Button>
              <Button variant="destructive" onClick={clearLoginHistory} className="rounded-2xl h-12 w-full sm:w-48 font-black uppercase tracking-widest bg-red-600 shadow-xl shadow-red-200">Execute Purge</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showClearAuditConfirm} onOpenChange={setShowClearAuditConfirm}>
          <DialogContent className="sm:max-w-md rounded-[3rem] p-10 border-none shadow-2xl animate-in slide-in-from-top-4">
            <DialogHeader className="items-center text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center animate-pulse">
                <Eraser className="w-10 h-10 text-red-600" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-black text-slate-900 tracking-tighter">Clear Audit Logs?</DialogTitle>
                <DialogDescription className="text-slate-400 font-medium leading-relaxed px-4">
                  All audit records for{" "}
                  <span className="font-black text-slate-700">
                    {filterDate === getLocalDateString() ? "today" : filterDate}
                  </span>{" "}
                  will be permanently erased from the server. This action cannot be undone.
                </DialogDescription>
              </div>
            </DialogHeader>
            <DialogFooter className="sm:justify-center gap-3 pt-8">
              <Button variant="ghost" onClick={() => setShowClearAuditConfirm(false)} disabled={clearingAudit} className="rounded-2xl h-12 w-full sm:w-32 font-bold text-slate-400">Abort</Button>
              <Button variant="destructive" onClick={handleClearAuditLogs} disabled={clearingAudit}
                className="rounded-2xl h-12 w-full sm:w-48 font-black uppercase tracking-widest bg-red-600 shadow-xl shadow-red-200 gap-2">
                {clearingAudit ? <><Loader2 className="w-4 h-4 animate-spin" /> Clearing…</> : <><Eraser className="w-4 h-4" /> Clear Logs</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* ── Info Modal ── */}
        <Dialog open={showInfoModal} onOpenChange={(open) => { setShowInfoModal(open); if (!open) setInfoAction(null); }}>
          <DialogContent className="sm:max-w-2xl rounded-[3rem] p-10 border-none shadow-2xl animate-in zoom-in-95 max-h-[90vh] flex flex-col overflow-hidden">
            <DialogHeader className="space-y-2 mb-4 shrink-0">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center shadow-lg shrink-0">
                    <Eye className="w-4 h-4 text-white" />
                  </div>
                  <div>
            <DialogTitle className="text-xl font-black text-slate-900 tracking-tighter leading-none">
  {formatRuleDisplayName(infoAction?.name ?? "Rule Details")}
</DialogTitle>
                  </div>
                </div>
                {infoAction && (
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-100 rounded-2xl px-3.5 py-2.5 shrink-0">
                    <div className="w-7 h-7 rounded-lg bg-slate-900 flex items-center justify-center font-black text-[9px] text-white shrink-0">
                      {(infoAction.username ?? "?").slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex flex-col leading-none">
                      <span className="text-[11px] font-black text-slate-700">{toTitleCase(infoAction.username ?? "—")}</span>
                      <span className="text-[10px] text-slate-400 font-bold">
                        {formatTimestamp(infoAction.timestamp).date} · {formatTimestamp(infoAction.timestamp).time}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </DialogHeader>

            <Separator className="bg-slate-100 shrink-0" />

            <div className="flex-1 overflow-y-auto py-4 space-y-2
              [&::-webkit-scrollbar]:w-1.5
              [&::-webkit-scrollbar-thumb]:rounded-full
              [&::-webkit-scrollbar-thumb]:bg-slate-200
              [&::-webkit-scrollbar-track]:bg-transparent">
              {infoAction && (() => {
                const info = infoAction.info ?? {};
                const changes = info.changes ?? {};
                const hasChanges = Object.keys(changes).length > 0;

                // Upload audit entries are intentionally compacted by the backend.
                // Example: errors: "[Array of 1150 items]". Render that payload
                // as a clear upload summary instead of exposing the raw placeholder.
                const isUploadAction = String(infoAction.type ?? "").trim().toLowerCase() === "upload";

                if (isUploadAction) {
                  const getCollapsedArrayCount = (value: unknown): number | null => {
                    if (Array.isArray(value)) return value.length;
                    if (typeof value !== "string") return null;

                    const match = value.trim().match(/^\[Array of (\d+) items\]$/i);
                    return match ? Number(match[1]) : null;
                  };

                  const errorCount = getCollapsedArrayCount(info.errors) ?? 0;
                  const hasUploadErrors = errorCount > 0;
                  const uploadSucceeded = info.success === true;
                  const uploadStatus = hasUploadErrors
                    ? "Completed with Errors"
                    : uploadSucceeded
                      ? "Completed Successfully"
                      : "Failed";

                  const rawMessage = typeof info.message === "string" ? info.message.trim() : "";
                  const uploadMessage = rawMessage
                    .replace(/^message:\s*['"]?/i, "")
                    .replace(/['"]\s*$/, "")
                    .trim();

                  const validRows = Array.isArray(info.validRows) ? info.validRows : [];
                  const collapsedValidRowsCount = getCollapsedArrayCount(info.validRows);

                  const formatUploadValue = (key: string, value: any): string | null => {
                    if (value === null || value === undefined || value === "") return null;
                    if (typeof value === "boolean") return value ? "Yes" : null;

                    if (Array.isArray(value)) {
                      if (value.length === 0) return null;
                      return value.map((item: any) => {
                        if (item && typeof item === "object") {
                          return item.name ?? item.Name ?? item.code ?? item.id ?? JSON.stringify(item);
                        }
                        return String(item);
                      }).join(", ");
                    }

                    if (typeof value === "string") {
                      const trimmed = value.trim();
                      if (!trimmed || trimmed.toUpperCase() === "DISABLED") return null;

                      if (key === "activity_type" || key === "activity_types") {
                        return trimmed
                          .split(",")
                          .map((type) => formatTypeLabel(type.trim()))
                          .filter(Boolean)
                          .join(", ");
                      }

                      return trimmed;
                    }

                    if (typeof value === "object") {
                      return value.name ?? value.Name ?? value.code ?? JSON.stringify(value);
                    }

                    return String(value);
                  };

                  const getUploadRowTitle = (row: any, index: number): string => {
                    return row?.relation_name
                      ?? row?.name
                      ?? row?.Provider_name
                      ?? row?.Policy_name
                      ?? `Row ${index + 1}`;
                  };

                  return (
                    <div className="space-y-3">
                      <p className="text-[10px] font-black tracking-widest text-slate-400 px-1">Upload Details</p>

                      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-slate-50">
                        <span className="text-xs font-black text-slate-500 shrink-0">Status</span>
                        <span className={cn(
                          "text-xs font-black px-3 py-1.5 rounded-lg border",
                          hasUploadErrors
                            ? "text-amber-700 bg-amber-50 border-amber-100"
                            : uploadSucceeded
                              ? "text-emerald-700 bg-emerald-50 border-emerald-100"
                              : "text-red-600 bg-red-50 border-red-100"
                        )}>
                          {uploadStatus}
                        </span>
                      </div>

                      {uploadMessage && (
                        <div className="flex items-start justify-between gap-4 px-4 py-3 rounded-xl bg-slate-50">
                          <span className="text-xs font-black text-slate-500 shrink-0 pt-1">Message</span>
                          <span className="text-xs font-bold text-slate-700 bg-white border border-slate-100 px-3 py-2 rounded-lg text-right break-words max-w-[70%]">
                            {uploadMessage}
                          </span>
                        </div>
                      )}

                      {hasUploadErrors && (
                        <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-slate-50">
                          <span className="text-xs font-black text-slate-500 shrink-0">Errors</span>
                          <span className="text-xs font-black text-red-600 bg-red-50 border border-red-100 px-3 py-1.5 rounded-lg">
                            {errorCount} {errorCount === 1 ? "error" : "errors"} recorded
                          </span>
                        </div>
                      )}

                      {(validRows.length > 0 || collapsedValidRowsCount !== null) && (
                        <div className="pt-2 space-y-2">
                          <div className="flex items-center justify-between gap-3 px-1">
                            <p className="text-[10px] font-black tracking-widest text-slate-400">Valid Rows</p>
                            <span className="text-[10px] font-black text-emerald-700 bg-emerald-50 border border-emerald-100 px-2.5 py-1 rounded-lg">
                              {validRows.length > 0 ? validRows.length : collapsedValidRowsCount} valid {((validRows.length > 0 ? validRows.length : collapsedValidRowsCount) === 1) ? "row" : "rows"}
                            </span>
                          </div>

                          {validRows.length > 0 ? (
                            <div className="space-y-2">
                              {validRows.map((row: any, rowIndex: number) => {
                                const rowEntries = Object.entries(row ?? {})
                                  .map(([key, value]) => ({ key, value: formatUploadValue(key, value) }))
                                  .filter(({ value }) => value !== null);

                                return (
                                  <div key={`upload-valid-row-${rowIndex}`} className="rounded-2xl border border-emerald-100 bg-emerald-50/30 overflow-hidden">
                                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-white/80 border-b border-emerald-100">
                                      <span className="text-xs font-black text-slate-800 break-words">
                                        {getUploadRowTitle(row, rowIndex)}
                                      </span>
                                      <span className="text-[9px] font-black tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-100 px-2 py-1 rounded-md shrink-0">
                                        Valid Row {rowIndex + 1}
                                      </span>
                                    </div>

                                    <div className="divide-y divide-emerald-100/70">
                                      {rowEntries.map(({ key, value }) => (
                                        <div key={`${rowIndex}-${key}`} className="flex items-start justify-between gap-4 px-4 py-2.5">
                                          <span className="text-[11px] font-black text-slate-500 shrink-0">
                                            {getFieldLabel(key, infoAction?.name ?? "")}
                                          </span>
                                          <span className="text-[11px] font-bold text-slate-700 text-right break-words max-w-[68%] whitespace-pre-line">
                                            {value}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-slate-50">
                              <span className="text-xs font-black text-slate-500">Valid Rows</span>
                              <span className="text-xs font-black text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-1.5 rounded-lg">
                                {collapsedValidRowsCount} rows recorded
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }

                const readableArray = (arr: any[]): string => {
                  if (!arr || arr.length === 0) return "—";
                  return arr.map((v: any) => {
                    if (typeof v !== "object" || v === null) return String(v);
                    return v.Policy_name ?? v.Provider_name ?? v.Plan_name ?? v.network_name
                      ?? v.Doctor_name ?? v.name ?? v.Name ?? v.Full_name ?? JSON.stringify(v);
                  }).join(", ");
                };

                const formatChangeVal = (v: any, key?: string): string => {
                  if (v === null || v === undefined) return "—";
                  if (typeof v === "boolean") return v ? "Yes" : "No";

                  // Update audit only: standardize Activity Type values.
                  // Example: cpt, hcpcs, dental → CPT, HCPCS, Dental
                  if (key === "activity_type" || key === "activity_types") {
                    return formatActivityTypeValue(v);
                  }

                  if (Array.isArray(v)) return readableArray(v);
                  if (typeof v === "string") return toTitleCase(v);
                  return String(v);
                };
 const SKIP_KEYS = [
  "id", "token", "changes", "data", "actionType", "target", "token_version",
  "vip_all", "user_all", "payer_all", "doctor_all", "policy_all",
  "patient_all", "provider_all", "specialty_all", "insuranceplan_all",
  "vip_mode", "user_mode", "payer_mode", "doctor_mode", "policy_mode",
  "patient_mode", "provider_mode", "specialty_mode", "insuranceplan_mode",
  "custom_ICD_groups", "medical_ICD_groups",
  "allowed_custom_groups", "allowed_medical_groups",
  "user_level", "force",
  "items_order",
];
                const dataSource = Array.isArray(info.data) && info.data.length > 0 ? info.data[0] : info;

                // Merge each code with its matching type by index, e.g. "25065 (CPT)"
                const mergeCodesWithTypes = (codes: any[] | undefined, types: any[] | undefined): string => {
                  const codeList = Array.isArray(codes) ? codes : [];
                  const typeList = Array.isArray(types) ? types : [];
                  if (codeList.length === 0) return "";
                  return codeList.map((code, i) => {
                    const codeStr = typeof code === "object" && code !== null
                      ? (code.code ?? JSON.stringify(code))
                      : String(code);
                    const typeStr = typeof typeList[i] === "object" ? typeList[i]?.type : typeList[i];
                    return typeStr ? `${codeStr} (${formatTypeLabel(typeStr)})` : codeStr;
                  }).join(", ");
                };

                // Prefer a fully-typed array (e.g. main_activity_types: [{code,type}]) when present;
                // otherwise fall back to zipping a plain codes array with a parallel types array.
                // This covers every page's naming convention (main_activity_types, types_main,
                // activity_types, activity_type, etc.) without needing one-off logic per page.
                const mergeTypedCodes = (
                  typedArr: any[] | undefined,
                  codes: any[] | undefined,
                  types: any[] | undefined
                ): string => {
                  if (Array.isArray(typedArr) && typedArr.length > 0) {
                    return typedArr.map((t: any) => {
                      const codeVal = typeof t === "object" && t !== null ? (t.code ?? JSON.stringify(t)) : t;
                      const typeVal = typeof t === "object" && t !== null ? t.type : undefined;
                      return typeVal ? `${codeVal} (${formatTypeLabel(typeVal)})` : String(codeVal);
                    }).join(", ");
                  }
                  return mergeCodesWithTypes(codes, types);
                };

                // Groups store {id, name} when resolved, or just an id/string otherwise
                const formatGroups = (groups: any[]): string => {
                  if (!Array.isArray(groups) || groups.length === 0) return "";
                  return groups.map((g: any) => {
                    if (typeof g === "object" && g !== null) return g.name ?? `Group #${g.id}`;
                    return `Group #${g}`;
                  }).join(", ");
                };

                const isActivityBundleLog = (() => {
                  const normalizedActionName = normalizePageKey(getRuleName(infoAction?.name ?? ""));
                  return normalizedActionName === "activitybundle" ||
                    normalizedActionName === "activityincludedinanotheractivity";
                })();

                const normalizeItemsOrder = (value: any): any[] => {
                  if (Array.isArray(value)) return value;
                  if (typeof value === "string") {
                    try {
                      const parsed = JSON.parse(value);
                      return Array.isArray(parsed) ? parsed : [];
                    } catch {
                      return [];
                    }
                  }
                  return [];
                };

                const getActivityBadgeClass = (type: any, itemType?: string): string => {
                  const normalizedType = String(type ?? "").trim().toLowerCase();
                  const normalizedItemType = String(itemType ?? "").trim().toLowerCase();

                  if (normalizedItemType === "medicalgroup") {
                    return "bg-amber-50 text-amber-700 border-amber-100";
                  }

                  if (normalizedItemType === "customgroup") {
                    return "bg-rose-50 text-rose-700 border-rose-100";
                  }

                  if (normalizedType === "cpt") {
                    return "bg-blue-50 text-blue-700 border-blue-100";
                  }

                  if (normalizedType === "hcpcs") {
                    return "bg-emerald-50 text-emerald-700 border-emerald-100";
                  }

                  if (normalizedType === "dental") {
                    return "bg-purple-50 text-purple-700 border-purple-100";
                  }

                  return "bg-slate-50 text-slate-700 border-slate-100";
                };

                const getActivityGroupCardClass = (variant: "previous" | "current" | "neutral"): string => {
                  if (variant === "previous") {
                    return "bg-red-50/40 border-red-100";
                  }

                  if (variant === "current") {
                    return "bg-emerald-50/40 border-emerald-100";
                  }

                  return "bg-slate-50/70 border-slate-200";
                };

                const renderActivityBundleGroups = (
                  value: any,
                  variant: "previous" | "current" | "neutral" = "neutral"
                ): React.ReactNode => {
                  const items = normalizeItemsOrder(value);
                  if (items.length === 0) return "—";

                  const byOrder = new Map<number, any[]>();
                  items.forEach((item: any) => {
                    const order = Number(item?.activity_order ?? 1);
                    if (!byOrder.has(order)) byOrder.set(order, []);
                    byOrder.get(order)!.push(item);
                  });

                  return (
                    <div className="space-y-2 text-left">
                      {Array.from(byOrder.entries())
                        .sort(([a], [b]) => a - b)
                        .map(([order, groupItems]) => (
                          <div
                            key={`activity-group-${order}`}
                            className={cn(
                              "rounded-xl border px-3 py-2 shadow-sm",
                              getActivityGroupCardClass(variant)
                            )}
                          >
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <span className="inline-flex items-center rounded-full bg-white border border-slate-200 px-2.5 py-1 text-[10px] font-black text-indigo-700 shadow-sm">
                                Activity {order}
                              </span>
                              <span className="text-[10px] font-black text-slate-400">
                                {groupItems.length} items
                              </span>
                            </div>

                            <div className="flex flex-wrap gap-2">
                              {groupItems.map((item: any, itemIndex: number) => {
                                const itemType = String(item?.type ?? "").trim().toLowerCase();

                                if (itemType === "medicalgroup") {
                                  return (
                                    <span
                                      key={`activity-${order}-medical-group-${itemIndex}`}
                                      className={cn(
                                        "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-black",
                                        getActivityBadgeClass("", "medicalgroup")
                                      )}
                                    >
                                      <span>Medical Group</span>
                                      <span className="font-black">{item?.name ?? `Group #${item?.id ?? ""}`}</span>
                                    </span>
                                  );
                                }

                                if (itemType === "customgroup") {
                                  return (
                                    <span
                                      key={`activity-${order}-custom-group-${itemIndex}`}
                                      className={cn(
                                        "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[10px] font-black",
                                        getActivityBadgeClass("", "customgroup")
                                      )}
                                    >
                                      <span>Custom Group</span>
                                      <span className="font-black">{item?.name ?? `Group #${item?.id ?? ""}`}</span>
                                    </span>
                                  );
                                }

                                const code = item?.code ?? item?.id ?? "—";
                                const activityType = item?.activity_type ? formatTypeLabel(item.activity_type) : "";

                                return (
                                  <span
                                    key={`activity-${order}-code-${itemIndex}`}
                                    className={cn(
                                      "inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-[10px] font-black",
                                      getActivityBadgeClass(item?.activity_type, itemType)
                                    )}
                                  >
                                    <span className="font-mono text-[11px]">{code}</span>
                                    {activityType && (
                                      <span className="text-[8px] font-black opacity-70">
                                        {activityType}
                                      </span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                    </div>
                  );
                };

                // Every "codes + parallel types" field seen across pages (two-sided Activity
                // One/Two rules AND single-list activity_code rules). All of these are excluded
                // from the generic "remaining keys" dump below and re-assembled explicitly, so
                // none of them ever render as raw JSON.
                const MERGED_KEYS = new Set([
                  // Activity One / main side
                  "main_activity", "main_activity_types", "types_main",
                  "main_activity_medicalgroups", "main_activity_customgroups",
                  // Activity Two / allowed side (ActivityAllowed)
                  "allowed_activity", "allowed_activity_types", "types_allowed",
                  "allowed_activity_medicalgroups", "allowed_activity_customgroups",
                  // Activity Two / not-allowed side (ActivityNotAllowed, ActivityNotAllowedIncludeActivity)
                  "not_allowed_activity", "not_allowed_activity_types", "types_not_allowed",
                  "not_allowed_activity_medicalgroups", "not_allowed_activity_customgroups",
                  // Single-list activity pages (ActivitiesReqirePreApprovals, ActivityAgeGender,
                  // ActivityNotCovered, ActivityFrequency, ActivityCustomGroup, ...)
                  "activity_code", "activity_type", "activity_types",
                  "medical_groups", "custom_groups",
                  "items_order",
                ]);

                const flatEntries: { key: string; value: React.ReactNode }[] = [];

                // Two-sided rules: Main side vs. an Allowed/Not-Allowed side (Activity One / Activity Two)
                const isTwoSided =
                  dataSource?.main_activity !== undefined || dataSource?.main_activity_types !== undefined ||
                  dataSource?.allowed_activity !== undefined || dataSource?.allowed_activity_types !== undefined ||
                  dataSource?.not_allowed_activity !== undefined || dataSource?.not_allowed_activity_types !== undefined;

                if (isTwoSided) {
                  // Activity One (main side)
                  {
                    const codesPart = mergeTypedCodes(
                      dataSource?.main_activity_types,
                      dataSource?.main_activity,
                      dataSource?.activity_type ?? dataSource?.types_main
                    );
                    const groupsPart = formatGroups([
                      ...(dataSource?.main_activity_medicalgroups ?? []),
                      ...(dataSource?.main_activity_customgroups ?? []),
                    ]);
                    const value = [codesPart, groupsPart].filter(Boolean).join(", ");
                    if (value) flatEntries.push({ key: "activity_one", value });
                  }

                  // Activity Two (allowed or not-allowed side, whichever is present)
                  {
                    const secondaryTyped = dataSource?.allowed_activity_types?.length
                      ? dataSource.allowed_activity_types
                      : dataSource?.not_allowed_activity_types;
                    const secondaryCodes = dataSource?.allowed_activity?.length
                      ? dataSource.allowed_activity
                      : dataSource?.not_allowed_activity;
                    const secondaryTypes = dataSource?.types_allowed?.length
                      ? dataSource.types_allowed
                      : (dataSource?.types_not_allowed?.length ? dataSource.types_not_allowed : dataSource?.activity_types);
                    const codesPart = mergeTypedCodes(secondaryTyped, secondaryCodes, secondaryTypes);
                    const groupsPart = formatGroups([
                      ...(dataSource?.allowed_activity_medicalgroups ?? []),
                      ...(dataSource?.allowed_activity_customgroups ?? []),
                      ...(dataSource?.not_allowed_activity_medicalgroups ?? []),
                      ...(dataSource?.not_allowed_activity_customgroups ?? []),
                    ]);
                    const groupedBundleValue = isActivityBundleLog && normalizeItemsOrder(dataSource?.items_order).length > 0
                      ? renderActivityBundleGroups(dataSource.items_order, "neutral")
                      : null;
                    const value = groupedBundleValue || [codesPart, groupsPart].filter(Boolean).join(", ");
                    if (value) flatEntries.push({ key: "activity_two", value });
                  }
                } else if (dataSource?.activity_code !== undefined || dataSource?.activity_types !== undefined) {
                  // Single-list activity pages: one codes list + its parallel type info
                  const codesPart = mergeTypedCodes(
                    dataSource?.activity_types,
                    dataSource?.activity_code,
                    dataSource?.activity_type
                  );
                  const groupsPart = formatGroups([
                    ...(dataSource?.medical_groups ?? []),
                    ...(dataSource?.custom_groups ?? []),
                  ]);
                  const value = [codesPart, groupsPart].filter(Boolean).join(", ");
                  if (value) flatEntries.push({ key: "activity_code_merged", value });
                }

                // Remaining keys
                Object.entries(dataSource ?? {})
                  .filter(([k, v]) => {
                    if (SKIP_KEYS.includes(k)) return false;
                    if (MERGED_KEYS.has(k)) return false;
                    if (v === null || v === undefined || v === "" || v === false) return false;
                    if (Array.isArray(v) && (v as any[]).length === 0) return false;
                    return true;
                  })
                  .forEach(([k, v]) => {
                    flatEntries.push({
                      key: k,
                      value: Array.isArray(v) ? readableArray(v as any[])
                        : typeof v === "boolean" ? (v ? "Yes" : "No")
                        : typeof v === "object" ? JSON.stringify(v)
                        : typeof v === "string" ? toTitleCase(v)
                        : String(v),
                    });
                  });

                if (hasChanges) {
                  // Merge every "type" change into its matching "code" change so
                  // Activity One / Activity Two show as "CODE (TYPE)" in one row,
                  // instead of duplicate rows when backend sends the same type info
                  // under more than one key (e.g. types_allowed AND activity_types).
                  const TYPE_KEY_MAP: Record<string, string[]> = {
                    main_activity: ["activity_type", "types_main", "main_activity_types"],
                    allowed_activity: ["types_allowed", "activity_types", "allowed_activity_types"],
                    not_allowed_activity: ["types_not_allowed", "activity_types", "not_allowed_activity_types"],
                    activity_code: ["activity_type", "activity_types"],
                  };
                  const consumedTypeKeys = new Set<string>();
                  Object.values(TYPE_KEY_MAP).forEach(typeKeys =>
                    typeKeys.forEach(tk => { if (changes[tk] !== undefined) consumedTypeKeys.add(tk); })
                  );

                  // Groups codes by their type and renders one line per type
                  // (e.g. "CPT: 0088U, 28490, 33949") instead of one long run-on
                  // list, so Activity One / Activity Two read cleanly.
                  const mergeCodeWithType = (codeVal: any, typeVal: any): string => {
                    const codes = Array.isArray(codeVal) ? codeVal : (codeVal !== undefined && codeVal !== null && codeVal !== "" ? [codeVal] : []);
                    const types = Array.isArray(typeVal) ? typeVal : (typeVal !== undefined && typeVal !== null && typeVal !== "" ? [typeVal] : []);
                    if (codes.length === 0) return "—";

                    const groups: { label: string; codes: string[] }[] = [];
                    codes.forEach((c: any, i: number) => {
                      const codeStr = typeof c === "object" && c !== null ? (c.code ?? JSON.stringify(c)) : String(c);
                      const rawType = types[i] ?? types[0];
                      const label = rawType ? formatTypeLabel(rawType) : "";
                      let group = groups.find(g => g.label === label);
                      if (!group) { group = { label, codes: [] }; groups.push(group); }
                      group.codes.push(codeStr);
                    });

                    return groups
                      .map(g => (g.label ? `${g.label}: ${g.codes.join(", ")}` : g.codes.join(", ")))
                      .join("\n");
                  };

                  const rows: { key: string; old: React.ReactNode; new: React.ReactNode }[] = [];
                  const bundleFlatKeys = new Set([
                    "not_allowed_activity", "not_allowed_activity_types", "types_not_allowed",
                    "activity_types", "not_allowed_activity_medicalgroups", "not_allowed_activity_customgroups",
                  ]);
                  Object.entries(changes).forEach(([key, val]: [string, any]) => {
                    if (isActivityBundleLog && changes.items_order !== undefined && bundleFlatKeys.has(key)) return;
                    if (consumedTypeKeys.has(key)) return;

                    if (isActivityBundleLog && key === "items_order") {
                      rows.push({
                        key: "not_allowed_activity",
                        old: renderActivityBundleGroups(val?.old, "previous"),
                        new: renderActivityBundleGroups(val?.new, "current"),
                      });
                      return;
                    }

                    const typeKey = TYPE_KEY_MAP[key]?.find(tk => changes[tk] !== undefined);
                    if (typeKey) {
                      rows.push({
                        key,
                        old: mergeCodeWithType(val?.old, changes[typeKey]?.old),
                        new: mergeCodeWithType(val?.new, changes[typeKey]?.new),
                      });
                    } else {
                      rows.push({ key, old: formatChangeVal(val?.old, key), new: formatChangeVal(val?.new, key) });
                    }
                  });

                  return (
                    <div className="space-y-2">
                      <p className="text-[10px] font-black tracking-widest text-slate-400 px-1">Changes</p>
                      {rows.map(({ key, old, new: newVal }) => (
                        <div key={key} className="rounded-xl border border-slate-100 overflow-hidden">
                          <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-100">
                            <span className="text-[10px] font-black text-slate-600">{getUpdateFieldLabel(key, infoAction?.name ?? "")}</span>
                          </div>
                          <div className="grid grid-cols-2 divide-x divide-slate-100">
                            <div className="px-3 py-2 bg-red-50/60">
                              <p className="text-[9px] font-black text-red-400 tracking-widest mb-1">Previous</p>
                              <div className="text-xs font-bold text-red-600 whitespace-pre-line break-words">{old}</div>
                            </div>
                            <div className="px-3 py-2 bg-emerald-50/60">
                              <p className="text-[9px] font-black text-emerald-400 tracking-widest mb-1">Current</p>
                              <div className="text-xs font-bold text-emerald-600 whitespace-pre-line break-words">{newVal}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }

                if (flatEntries.length > 0) {
                  return (
                    <div className="space-y-2">
                      <p className="text-[10px] font-black tracking-widest text-slate-400 px-1">
                        {infoAction.type === "delete" ? "DELETED RECORD" : "RECORD"}
                      </p>
                      {flatEntries.map(({ key, value }) => (
                        <div key={key} className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100/80 transition-colors">
                          <span className="text-xs font-black text-slate-500 shrink-0">
                            {getFieldLabel(key, infoAction?.name ?? "")}
                          </span>

                          <div className={cn(
                            "text-xs font-bold rounded-lg break-words text-right",
                            typeof value === "string"
                              ? value === "Yes"
                                ? "text-emerald-600 bg-emerald-50 px-2.5 py-1"
                                : value === "No"
                                  ? "text-red-400 bg-red-50 px-2.5 py-1"
                                  : "text-slate-700 bg-white border border-slate-100 px-2.5 py-1"
                              : "flex-1 bg-transparent border-none p-0"
                          )} style={{ whiteSpace: typeof value === "string" ? "pre-line" : "normal" }}>{value}</div>
                        </div>
                      ))}
                    </div>
                  );
                }

                const actionName = toTitleCase(infoAction.name ?? "");
                return (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl bg-slate-50">
                      <span className="text-xs font-black text-slate-500 shrink-0">Action</span>
                      <span className="text-xs font-bold px-2.5 py-1 rounded-lg text-slate-700 bg-white border border-slate-100">{actionName}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100 shrink-0">
              <Button variant="ghost" onClick={() => setShowInfoModal(false)}
                className="rounded-2xl h-12 px-8 font-bold text-slate-400 hover:text-slate-900">
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="sm:max-w-md rounded-[3rem] p-10 border-none shadow-2xl animate-in slide-in-from-top-4">
            <DialogHeader className="items-center text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-red-50 flex items-center justify-center animate-pulse">
                <Trash2 className="w-10 h-10 text-red-600" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-black text-slate-900 tracking-tighter">Delete User?</DialogTitle>
                <DialogDescription className="text-slate-400 font-medium leading-relaxed px-4">
                  User <span className="font-black text-slate-700">{toTitleCase(deletingUser?.name ?? "")}</span> will be Permanently Deleted. Their activity logs will remain linked to their username.
                </DialogDescription>
              </div>
            </DialogHeader>
            <DialogFooter className="sm:justify-center gap-3 pt-8">
              <Button variant="ghost" onClick={() => { setShowDeleteConfirm(false); setDeletingUser(null); }} className="rounded-2xl h-12 w-full sm:w-32 font-bold text-slate-400">Cancel</Button>
              <Button variant="destructive" disabled={deleting === deletingUser?.id}
                onClick={() => { if (deletingUser) { handleDelete(deletingUser.id, deletingUser.name); setShowDeleteConfirm(false); } }}
                className="rounded-2xl h-12 w-full sm:w-48 font-black uppercase tracking-widest bg-red-600 shadow-xl shadow-red-200 gap-2">
                {deleting === deletingUser?.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Deleting…</> : <><Trash2 className="w-4 h-4" /> Delete User</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Toaster />
      </div>
    </TooltipProvider>
  );
};

export default UserManagement;